/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppDispatch, RootState } from '@/store';
import {
  DistinctPersonnelGroup,
  PersonnelGroup,
  ResourceClassTree,
  ResourceSubClassTree,
  ShutdownTree,
  SiteTree,
  TreeItem,
  TSimulationMenuOptions,
  WipItemParams,
  WipTree,
  WipTreeElement,
} from '@/store/types';
import { Table } from '@data-products-and-ai/react-components';
import {
  TableHeaderType,
  TableRowData,
} from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';
import React, { ReactNode, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContextMenu } from '../ContextMenu';
import { TAnchorEl } from '@data-products-and-ai/react-components/lib/components/Layout/Menu/Menu';
import { changeExpanded } from '@/store/storeSlice';
import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import MultiEditModal from '@/components/MultiEditModal/MultiEditModal';

interface ProductTreeRender extends WipItemParams {
  PERSONNEL_GROUP: TreeItem;
  PERSONNEL_GROUP_ID: TreeItem;
  RESOURCE_CLASS_ID: TreeItem;
  RESOURCE_CLASS: TreeItem;
  RESOURCE_SUB_CLASS: TreeItem;
  RESOURCE_SUB_CLASS_ID: TreeItem;
}

type ProductTreeRenderGeneric = {
  [K in keyof ProductTreeRender]: string | number | ReactNode;
};

type TProductStructure = {
  id: string;
  data: ProductTreeRenderGeneric;
  children: TProductStructure[];
  isClickable?: boolean;
  isExpanded?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  isSelectable?: boolean;
  area: TSimulationMenuOptions;
};

const TableHeaders: TableHeaderType[] = [
  {
    title: 'Work Unit Class',
    align: 'left',
    columnKey: 'RESOURCE_CLASS',
    columnWidth: '400px',
  },
  {
    title: 'Personnel Group',
    align: 'center',
    columnKey: 'PERSONNEL_GROUP',
  },
];

function findAndPushItem(
  obj: TProductStructure[],
  idToFind: string,
  newItem: TProductStructure,
): boolean {
  for (const item of obj) {
    if (item.id === idToFind) {
      if (!item.children) {
        item.children = []; // Ensure children array exists before pushing
      }
      item.children.push(newItem);

      item.children.sort((a, b) => {
        const stepA = (a.data.PERSONNEL_GROUP as string) || ''; // Use 0 as a default value if STEP.value is null or undefined
        const stepB = (b.data.PERSONNEL_GROUP as string) || ''; // Use 0 as a default value if STEP.value is null or undefined

        return stepA.localeCompare(stepB);
      });
      return true; // Return true to stop further recursion once the item is found
    } else if (item.children) {
      if (findAndPushItem(item.children, idToFind, newItem)) {
        return true; // If the item is found in the child, stop further recursion
      }
    }
  }
  return false; // Return false if the item is not found in the current objects or its children
}

const generateObjectWithChildren = (
  sitesObject: WipTreeElement<SiteTree>,
  personnelGroupObject: WipTreeElement<PersonnelGroup>,
  resourceClassObject: WipTreeElement<ResourceClassTree>,
  resourcesSubClassesObject: WipTreeElement<ResourceSubClassTree>,
): TProductStructure[] => {
  const objectWithChildren: any[] = [];
  const itemMap: { [itemId: string]: TProductStructure } = {};
  const siteIds: string[] = [];

  sitesObject.allIds.forEach((itemId: string) => {
    const item = sitesObject.byId[itemId];

    const newItem: TProductStructure = {
      id: item.SITE_ID.originalValue,
      area: 'sites',
      isExpanded: item.is_open,
      isDisabled: item.is_disabled,
      data: {
        key: item.SITE_ID.originalValue,
        title: item.SITE.value,
        is_wip: item.is_wip,
        is_deleted: item.is_deleted,
        is_disabled: item.is_disabled,
        is_clicked: item.is_clicked,
        is_selectable: item.is_selectable,
        is_selected: item.is_selected,
        is_hidden: item.is_hidden,
        is_match: item.is_match,
        is_open: item.is_open,
        parent: '',
        PERSONNEL_GROUP_ID: '',
        PERSONNEL_GROUP: '',
        RESOURCE_CLASS_ID: item.SITE.value,
        RESOURCE_CLASS: item.SITE.value,
        RESOURCE_SUB_CLASS: '',
        RESOURCE_SUB_CLASS_ID: '',
        children: [],
      },
      children: [],
    };
    siteIds.push(item.SITE_ID.originalValue);
    objectWithChildren.push(newItem);
    itemMap[itemId] = newItem;
  });

  resourceClassObject.allIds.forEach((itemId: string) => {
    const item = resourceClassObject.byId[itemId];
    const personnelGroups = [
      ...new Set(
        personnelGroupObject.allIds
          .map((id) => personnelGroupObject.byId[id])
          .filter((personnelGroup) => {
            const siteId = personnelGroup.SITE_ID.originalValue;
            return (
              personnelGroup.RESOURCE_CLASS_ID.value ===
                item.RESOURCE_CLASS_ID.value && siteIds.includes(siteId!)
            );
          })
          .map((personnelGroup) => {
            return personnelGroup.PERSONNEL_GROUP.value;
          }),
      ),
    ];

    const newItem: TProductStructure = {
      id: item.RESOURCE_CLASS_ID.originalValue,
      area: 'resource_classes',
      isExpanded: item.is_open,
      isDisabled: item.is_disabled,
      data: {
        key: item.RESOURCE_CLASS_ID.originalValue,
        title: item.RESOURCE_CLASS.value,
        is_wip: item.is_wip,
        is_deleted: item.is_deleted,
        is_disabled: item.is_disabled,
        is_clicked: item.is_clicked,
        is_selectable: item.is_selectable,
        is_selected: item.is_selected,
        is_hidden: item.is_hidden,
        is_match: item.is_match,
        is_open: item.is_open,
        parent: '',
        PERSONNEL_GROUP_ID: '',
        PERSONNEL_GROUP: personnelGroups.join(', '),
        RESOURCE_CLASS_ID: item.RESOURCE_CLASS_ID.originalValue,
        RESOURCE_CLASS: item.RESOURCE_CLASS.originalValue,
        RESOURCE_SUB_CLASS: '',
        RESOURCE_SUB_CLASS_ID: '',
        children: [],
      },
      children: [],
    };
    siteIds.forEach((siteId: string) => {
      findAndPushItem(objectWithChildren, siteId, newItem);
      itemMap[itemId] = newItem;
    });
  });

  personnelGroupObject.allIds.forEach((itemId: string) => {
    const item = personnelGroupObject.byId[itemId];

    console.log('personnel group: ', item);
    console.log(
      'subclass: ',
      resourcesSubClassesObject.byId[item.RESOURCE_SUBCLASS_ID.value],
    );
    console.log(
      'class: ',
      resourceClassObject.byId[item.RESOURCE_CLASS_ID.value],
    );
  });

  resourcesSubClassesObject.allIds.forEach((itemId: string) => {
    const item = resourcesSubClassesObject.byId[itemId];
    const classId = item.RESOURCE_CLASS_ID.value;
    const personnelGroups = [
      ...new Set(
        personnelGroupObject.allIds
          .map((id) => personnelGroupObject.byId[id])
          .filter((personnelGroup) => {
            const siteId = personnelGroup.SITE_ID.originalValue;
            return (
              personnelGroup.RESOURCE_SUBCLASS_ID.value ===
                item.RESOURCE_SUBCLASS_ID.value && siteIds.includes(siteId!)
            );
          })
          .map((personnelGroup) => {
            return personnelGroup.PERSONNEL_GROUP.value;
          }),
      ),
    ];

    const newItem: TProductStructure = {
      id: item.RESOURCE_SUBCLASS_ID.value,
      area: 'resource_sub_classes',
      isExpanded: item.is_open,
      isDisabled: item.is_disabled,
      data: {
        key: item.RESOURCE_SUBCLASS_ID.value,
        title: item.RESOURCE_SUBCLASS.value,
        is_wip: item.is_wip,
        is_deleted: item.is_deleted,
        is_disabled: item.is_disabled,
        is_clicked: item.is_clicked,
        is_selectable: item.is_selectable,
        is_selected: item.is_selected,
        is_hidden: item.is_hidden,
        is_match: item.is_match,
        is_open: item.is_open,
        parent: '',
        PERSONNEL_GROUP_ID: '',
        PERSONNEL_GROUP: personnelGroups.join(', '),
        RESOURCE_CLASS_ID: '',
        RESOURCE_CLASS: item.RESOURCE_SUBCLASS.value,
        RESOURCE_SUB_CLASS: item.RESOURCE_SUBCLASS.value,
        RESOURCE_SUB_CLASS_ID: item.RESOURCE_SUBCLASS_ID.value,
        children: [],
      },

      children: [],
    };
    findAndPushItem(objectWithChildren, classId, newItem);
    itemMap[itemId] = newItem;
  });

  /* distinctPersonnelGroupObject.allIds.forEach((itemId: string) => {
    const item = distinctPersonnelGroupObject.byId[itemId];
    personnelGroupObject.allIds.forEach((personnelGroupId: string) => {
      const personnelGroup = personnelGroupObject.byId[personnelGroupId];
      const siteId = personnelGroup.SITE_ID.originalValue;
      const subclassID = personnelGroup.RESOURCE_SUBCLASS_ID.value;

      if (!siteId || !sitesObject.byId[siteId]) return;
      if (
        personnelGroup.PERSONNEL_GROUP_ID.value ===
        item.PERSONNEL_GROUP_ID.value
      ) {
        const resourceClass = resourceClassObject.allIds
          .map((id) => {
            const obj = resourceClassObject.byId[id];
            if (
              obj.RESOURCE_CLASS_ID.value ===
              personnelGroup.RESOURCE_CLASS_ID.value
            ) {
              return obj.RESOURCE_CLASS.originalValue;
            }
            return null;
          })
          .filter((item) => item !== null);
        
        const resourceSubClass = resourcesSubClassesObject.allIds
          .map((id) => {
            const obj = resourcesSubClassesObject.byId[id];
            if (
              obj.RESOURCE_SUBCLASS_ID.value ===
              personnelGroup.RESOURCE_SUBCLASS_ID.value
            ) {
              return obj.RESOURCE_SUBCLASS_ID.originalValue;
            }
            return null;
          })
          .filter((item) => item !== null);

        const newItem: TProductStructure = {
          id: personnelGroup.PERSONNEL_GROUP_ID.originalValue ?? '',
          area: 'personnel_group',
          isExpanded: item.is_open,
          isDisabled: item.is_disabled,
          data: {
            key: personnelGroup.SITE_ID.originalValue,
            title: personnelGroup.PERSONNEL_GROUP.value,
            is_wip: item.is_wip,
            is_deleted: item.is_deleted,
            is_disabled: item.is_disabled,
            is_clicked: item.is_clicked,
            is_selectable: item.is_selectable,
            is_selected: item.is_selected,
            is_hidden: item.is_hidden,
            is_match: item.is_match,
            is_open: item.is_open,
            parent: '',
            PERSONNEL_GROUP_ID: personnelGroup.PERSONNEL_GROUP_ID.originalValue,
            PERSONNEL_GROUP: personnelGroup.PERSONNEL_GROUP.originalValue,
            RESOURCE_CLASS_ID: personnelGroup.RESOURCE_CLASS_ID.originalValue,
            RESOURCE_CLASS: resourceClass,
            RESOURCE_SUB_CLASS: resourceSubClass,
            RESOURCE_SUB_CLASS_ID: personnelGroup.RESOURCE_SUBCLASS_ID.originalValue,
            children: [],
          },
          children: [],
        };
        findAndPushItem(objectWithChildren, subclassID, newItem);
        itemMap[itemId] = newItem;
      }
    });
  }); */

  return objectWithChildren;
};

/**
 * Shutdowns Component
 * Renders a table of shutdowns with various functionalities such as filtering, expanding rows, and context menu actions
 * Manages the state for selected row, anchor element, and expanded years
 */
const PersonnelGroups = () => {
  const { setSimulationParams } = useContext(SimulationCtx);
  const [anchorEl, setAnchorEl] = useState<TAnchorEl>(null);
  const [selectedRow, setSelectedRow] = useState<TableRowData | null>(null);
  const dispatch: AppDispatch = useDispatch();

  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected),
  );

  const handleDoubleClick = (value: TableRowData) => {
    const area = value.area as TSimulationMenuOptions;
    if (area !== 'personnel_group') return;

    setSimulationParams((prevState) => ({
      ...prevState,
      drawerOpen: true,
      formItem: value.id,
      formArea: area,
    }));
  };

  const handleClick = (value: TableRowData) => {
    if (!value.children) return;
    if (value.children.length <= 0) return;
    dispatch(
      changeExpanded({
        id: value.id,
        isExpanded: value.isExpanded ?? false,
        area: value.area as keyof WipTree,
      }),
    );
  };

  const handleMenuRight = (
    value: TableRowData,
    event?: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    const area = value.area as keyof WipTree;
    if (area !== 'resource_classes' && area !== 'resource_sub_classes') return;
    event &&
      setAnchorEl({
        left: event.clientX,
        top: event.clientY,
      });

    setSelectedRow(value);
  };

  /**
   * memoizedObj Constant
   * Memoizes the object with children to optimize rendering
   * Applies the expanded state to the shutdown_years items
   *
   * @returns {TProductStructure[]} - The memoized array of product structure objects
   */
  const memoizedObj = React.useMemo(() => {
    if (!selectedScenario) return [];
    const obj = generateObjectWithChildren(
      selectedScenario.data.sites,
      selectedScenario.data.personnel_group,
      selectedScenario.data.resource_classes,
      selectedScenario.data.resource_sub_classes,
    );
    return obj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScenario]);

  /**
   * memoizedTable Constant
   * Memoizes the table component to optimize rendering
   *
   * @returns {JSX.Element} - The memoized table component
   */
  const memoizedTable = React.useMemo(() => {
    return (
      <Table
        headers={TableHeaders}
        width={'100%'}
        isSelectable={false}
        hasFilter={true}
        rows={memoizedObj}
        typographyTag="textsmall"
        pointerCursor={true}
        onRowDoubleClick={(value) => handleDoubleClick(value)}
        onRowClick={(value) => handleClick(value)}
        onRowRightClick={(value, event) => handleMenuRight(value, event)}
        stickyHeader={true}
        headerVerticalAlign="bottom"
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedObj]);

  if (!selectedScenario) return <>No Scenario</>;

  const additionalOptions = {
    PERSONNEL_GROUP: selectedScenario.data.personnel_group.allIds.map((id) => {
      const obj = selectedScenario.data.personnel_group.byId[id];
      return {
        label: obj.PERSONNEL_GROUP.value,
        value: obj.PERSONNEL_GROUP.value,
      };
    }),
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: 10,
          marginBottom: 10,
        }}
      >
        {/*         <MultiEditModal area="personnel_group" keyProp="PERSONNEL_GROUP_PK" name='PERSONNEL_GROUP' additionalOptions={additionalOptions}/>
         */}{' '}
      </div>
      <ContextMenu
        selectedRow={selectedRow}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        showDuplicate={false}
        showRemove={false}
      />

      {memoizedTable}
    </>
  );
};

export default PersonnelGroups;
